import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.svg';

const styles = {
  navbar: {
    backgroundColor: '#fff',
    padding: '0 32px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'fixed',
    top: 0, left: 0, right: 0,
    zIndex: 100,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    borderBottom: '1px solid #e5e7eb',
  },
  left: { display: 'flex', alignItems: 'center', gap: '10px' },
  badge: {
    backgroundColor: '#EBF5FF', color: '#0052CC',
    fontSize: '10px', fontWeight: '700',
    padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.5px',
  },
  right: { display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' },
  avatarBtn: {
    width: '36px', height: '36px', borderRadius: '50%',
    backgroundColor: '#0052CC', border: '2px solid #0052CC',
    color: '#fff', fontSize: '13px', fontWeight: '700',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', letterSpacing: '0.5px', flexShrink: 0,
  },
  dropdown: {
    position: 'absolute', top: '48px', right: 0,
    backgroundColor: '#fff', borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    border: '1px solid #e5e7eb', minWidth: '220px',
    zIndex: 200, overflow: 'hidden',
  },
  dropdownHeader: {
    padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
    backgroundColor: '#f9fafb',
  },
  dropdownName: { fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '2px' },
  dropdownRole: { fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'capitalize' },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '11px 20px', fontSize: '14px', fontWeight: '500',
    color: '#374151', cursor: 'pointer', border: 'none',
    background: 'none', width: '100%', textAlign: 'left',
    fontFamily: 'inherit',
  },
  dropdownDivider: { height: '1px', backgroundColor: '#f3f4f6', margin: '4px 0' },
  dropdownSignOut: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '11px 20px', fontSize: '14px', fontWeight: '500',
    color: '#dc2626', cursor: 'pointer', border: 'none',
    background: 'none', width: '100%', textAlign: 'left',
    fontFamily: 'inherit',
  },
};

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

export default function Navbar({ onMenuToggle, isMobile, sidebarOpen }) {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleLogout() {
    setOpen(false);
    await logout();
    navigate('/login');
  }

  function handleNavigate(path) {
    setOpen(false);
    navigate(path);
  }

  const displayName = userProfile?.displayName || currentUser?.email || 'User';
  const role = userProfile?.role || 'researcher';
  const plan = userProfile?.plan || 'standard';
  const initials = userProfile?.displayName
    ? getInitials(userProfile.displayName)
    : (currentUser?.email?.[0] || '?').toUpperCase();

  return (
    <nav style={{ ...styles.navbar, padding: isMobile ? '0 16px' : '0 32px' }}>
        <div style={styles.left}>
          <button
            onClick={onMenuToggle}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#374151',
            }}
            aria-label="Toggle sidebar"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <img
            src={logo}
            alt="V2VLab"
            style={{ height: '28px', cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          />
          {!isMobile && <div style={styles.badge}>BETA</div>}
        </div>

      <div style={styles.right} ref={dropdownRef}>
        <button style={styles.avatarBtn} onClick={() => setOpen(prev => !prev)}>
          {initials}
        </button>

        {open && (
          <div style={styles.dropdown}>
            <div style={styles.dropdownHeader}>
              <div style={styles.dropdownName}>{displayName}</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>{currentUser?.email}</div>
              <div style={styles.dropdownRole}>
                {role}
                <span style={{
                  marginLeft: '8px', fontSize: '10px', fontWeight: '700',
                  padding: '1px 6px', borderRadius: '10px',
                  backgroundColor: plan === 'enterprise' ? '#fef3c7' : plan === 'professional' ? '#dbeafe' : '#f3f4f6',
                  color: plan === 'enterprise' ? '#d97706' : plan === 'professional' ? '#0052CC' : '#6b7280',
                }}>
                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                </span>
              </div>
            </div>

            <div style={{ padding: '4px 0', display: 'flex', flexDirection: 'column' }}>
              <button style={styles.dropdownItem} onClick={() => handleNavigate('/profile')}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                👤 My Profile
              </button>
              <button style={styles.dropdownItem} onClick={() => handleNavigate('/settings')}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                ⚙️ Settings
              </button>
              {plan === 'standard' && (
                <button
                  style={{ ...styles.dropdownItem, color: '#0052CC', fontWeight: '700' }}
                  onClick={() => handleNavigate('/pricing')}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EBF5FF'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  ✦ Upgrade to Professional
                </button>
              )}
              {plan === 'professional' && (
                <button style={styles.dropdownItem} onClick={() => handleNavigate('/profile')}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  💳 Manage Subscription
                </button>
              )}
              <div style={styles.dropdownDivider} />
              <button style={styles.dropdownSignOut} onClick={handleLogout}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                → Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
